import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import L from 'leaflet';
import { ILayer } from '../../../models';

//#region Interfaces
interface IVisibleGeoFeatureReference {
  layerName: string;
  color: string;
  url: string;
}

//#region Helper Functions
function extractUrls(value: string): string[] {
  if (!value) return [];

  const urlMatches = value.match(/https?:\/\/[^\s,;|]+/gi) || [];
  const normalizedUrls = urlMatches.map(url => url.replace(/[),.;]+$/, '').trim());

  return normalizedUrls.filter((url, index, self) => !!url && self.indexOf(url) === index);
}

function generateUniqueColor(index: number): string {
  const basePalette = [
    '#D81B60', '#1E88E5', '#43A047', '#FB8C00', '#6D4C41',
    '#00ACC1', '#C0CA33', '#8E24AA', '#3949AB', '#F4511E',
    '#E53935', '#5E35B1', '#00897B', '#FF6F00', '#546E7A',
    '#7CB342', '#F4511E', '#6D4C41', '#039BE5', '#D81B60'
  ];
  
  if (index < basePalette.length) {
    return basePalette[index];
  }
  
  // Generate additional colors using HSL for uniqueness
  const hue = (index * 137.5) % 360; // Golden angle for good distribution
  const saturation = 65 + (index % 3) * 10;
  const lightness = 45 + (index % 2) * 10;
  return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
}

async function loadImageAsDataUrl(url: string): Promise<string | null> {
  try {
    const response = await fetch(url, { mode: 'cors' });
    if (!response.ok) return null;

    const blob = await response.blob();
    return await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.error('Failed to load legend image', url, error);
    return null;
  }
}

function getVisibleGeoFeatureReferences(
  mapRef: any,
  layers: ILayer[],
  wfsFeatures: { [layerName: string]: any }
): IVisibleGeoFeatureReference[] {
  if (!mapRef || !mapRef.leafletElement) return [];

  const mapBounds = mapRef.leafletElement.getBounds();
  const references: IVisibleGeoFeatureReference[] = [];

  layers
    .filter(layer => layer.enabled && layer.type === 'wfs')
    .forEach(layer => {
      const geoJson = wfsFeatures[layer.name];
      if (!geoJson || !Array.isArray(geoJson.features)) return;

      geoJson.features.forEach((feature: any) => {
        const rawUrls = feature?.properties?._wfsUrls || feature?.properties?.Wasserrahmenrichtlinie_;
        const urls = Array.isArray(rawUrls) ? rawUrls : extractUrls(String(rawUrls || ''));
        const color = feature?.properties?._wfsFeatureColor || '#d32f2f';
        const coordinates = feature?.geometry?.coordinates;
        if (urls.length === 0 || !coordinates) return;

        let isVisible = false;
        const inspectCoordinates = (coords: any): void => {
          if (isVisible || !Array.isArray(coords)) return;

          if (coords.length >= 2 && typeof coords[0] === 'number' && typeof coords[1] === 'number') {
            const lng = coords[0];
            const lat = coords[1];
            isVisible = mapBounds.contains(L.latLng(lat, lng));
            return;
          }

          coords.forEach((child: any) => inspectCoordinates(child));
        };

        inspectCoordinates(coordinates);

        if (isVisible) {
          urls.forEach((url: string) => {
            references.push({
              layerName: layer.name,
              color,
              url
            });
          });
        }
      });
    });

  // Remove duplicates
  const uniqueReferences = references.filter((value, index, self) =>
    index === self.findIndex(v => v.layerName === value.layerName && v.color === value.color && v.url === value.url)
  );

  // Reassign unique colors to ensure no duplicates in the documentation
  const usedColors = new Set<string>();
  let colorIndex = 0;

  return uniqueReferences.map(ref => {
    let newColor = ref.color;
    
    // If this color is already used, assign a new unique color
    if (usedColors.has(newColor)) {
      do {
        newColor = generateUniqueColor(colorIndex++);
      } while (usedColors.has(newColor));
    }
    
    usedColors.add(newColor);
    
    return {
      ...ref,
      color: newColor
    };
  });
}

//#region Main Export
export async function generateDocumentation(
  mapRef: any, 
  layers: ILayer[], 
  wfsFeatures: { [layerName: string]: any }
): Promise<string | null> {
  if (!mapRef || !mapRef.leafletElement) {
    console.error('Map reference is not ready');
    alert('Kortet er ikke klar endnu. Prøv igen om lidt.');
    return null;
  }

  const maps = document.querySelectorAll('.leaflet-overlay-pane .leaflet-zoom-animated');
  console.log(`Found ${maps.length} map overlay elements`);
  const originals: Array<{ top: string; left: string }> = [];

  // These legends need to be big to be readable, so keep them out of the dialog screenshot and render them on their own page instead
  const fullPageLegendLayers = layers.filter(l => l.enabled && l.legendUrl && (l.name === 'Forstwege' || l.name === 'Wendemoeglichkeiten'));
  const hiddenLegendElements: Array<{ element: HTMLElement; display: string }> = [];
  fullPageLegendLayers.forEach(layer => {
    const element = document.querySelector(`[data-pdf-fullpage-legend="${layer.name}"]`) as HTMLElement | null;
    if (element) {
      hiddenLegendElements.push({ element, display: element.style.display });
      element.style.display = 'none';
    }
  });

  try {
    for (let i = 0; i < maps.length; ++i) {
      const map = maps[i] as HTMLElement;
      originals.push({ top: map.style.top, left: map.style.left });

      if (map.style.transform && map.style.transform.indexOf('translate3d(') >= 0) {
        const coordinates = map.style.transform.split('(')[1].split(')')[0].split(',');
        map.style.top = -1 * parseInt(coordinates[1].replace('px', ''), 10) + 'px';
        map.style.left = -1 * parseInt(coordinates[0].replace('px', ''), 10) + 'px';
      }
    }

    await new Promise(resolve => setTimeout(resolve, 200));

    // Capture the whole dialog (map + existing left-menu legend) so the legend appears in the screenshot itself.
    const dialogElement = document.querySelector('#documentationDialogOuter') as HTMLElement | null;
    const mapContainer = mapRef.leafletElement.getContainer() as HTMLElement;
    const captureTarget = dialogElement && document.body.contains(dialogElement) ? dialogElement : mapContainer;
    if (!captureTarget || !document.body.contains(captureTarget)) {
      throw new Error('Map container is not attached to the DOM');
    }

    const canvas = await html2canvas(captureTarget, {
      useCORS: true,
      backgroundColor: '#ffffff',
      removeContainer: true,
      logging: false
    });

    const mapImage = canvas.toDataURL();

    const pdf = new jsPDF('landscape', 'px', 'a4');
    const imgProps = pdf.getImageProperties(mapImage);
    const pdfWidth = pdf.internal.pageSize.getWidth() - 5;
    const pageHeight = pdf.internal.pageSize.getHeight();
    const pdfHeight = Math.min((imgProps.height * pdfWidth) / imgProps.width, pageHeight - 10);

    pdf.addImage(mapImage, 5, 5, pdfWidth - 5, pdfHeight);

    const loadedLegends = (await Promise.all(fullPageLegendLayers.map(async layer => {
      const dataUrl = await loadImageAsDataUrl(layer.legendUrl!);
      return dataUrl ? { name: layer.name, dataUrl } : null;
    }))).filter((legend): legend is { name: string; dataUrl: string } => legend !== null);

    if (loadedLegends.length > 0) {
      pdf.addPage('a4', 'landscape');

      const columnWidth = (pdf.internal.pageSize.getWidth() - 30) / loadedLegends.length;
      const maxLegendHeight = pdf.internal.pageSize.getHeight() - 40;

      loadedLegends.forEach((legend, index) => {
        const columnX = 10 + index * columnWidth;

        pdf.setFontSize(14);
        pdf.text(`${legend.name} - Signaturforklaring`, columnX, 20);

        const legendImgProps = pdf.getImageProperties(legend.dataUrl);
        const maxLegendWidth = columnWidth - 10;
        const scale = Math.min(maxLegendWidth / legendImgProps.width, maxLegendHeight / legendImgProps.height);
        const legendWidth = legendImgProps.width * scale;
        const legendHeight = legendImgProps.height * scale;
        const legendX = columnX + (maxLegendWidth - legendWidth) / 2;

        pdf.addImage(legend.dataUrl, legendX, 30, legendWidth, legendHeight);
      });
    }

    const featureReferences = getVisibleGeoFeatureReferences(mapRef, layers, wfsFeatures);
    if (featureReferences.length > 0) {
      pdf.addPage('a4', 'landscape');
      pdf.setFontSize(14);
      pdf.text('Synlige geofeatures (farve og URL)', 10, 20);

      pdf.setFontSize(10);
      let y = 36;
      const maxLineWidth = pdf.internal.pageSize.getWidth() - 20;

      featureReferences.forEach((reference, index) => {
        const description = `${index + 1}. Lag: ${reference.layerName} | Farve: ${reference.color} | URL: ${reference.url}`;
        const lines = pdf.splitTextToSize(description, maxLineWidth - 16) as string[];

        if (y + lines.length * 14 > pdf.internal.pageSize.getHeight() - 10) {
          pdf.addPage('a4', 'landscape');
          y = 20;
        }

        pdf.setFillColor(reference.color);
        pdf.rect(10, y - 8, 8, 8, 'F');
        pdf.setTextColor(20, 20, 20);
        pdf.text(lines, 22, y);
        y += lines.length * 14;
      });
    }

    // Return the PDF as a data URI string for upload
    console.log('PDF generation completed successfully');
    return pdf.output('datauristring');
  } catch (error) {
    console.error('Error while generating test PDF:', error);
    alert('Der opstod en fejl ved generering af test-PDF.');
    return null;
  } finally {
    for (let i = 0; i < maps.length; ++i) {
      const map = maps[i] as HTMLElement;
      map.style.top = originals[i].top;
      map.style.left = originals[i].left;
    }

    hiddenLegendElements.forEach(({ element, display }) => { element.style.display = display; });
  }
}